import { useState } from 'react'
import { useI18n, type MessageKey } from '../i18n'
import { clamp, COLORS, DEFAULT_STYLE, DELAY_RANGE, FONT_FAMILIES, SIZE_RANGE, styleVars, type SubtitleEdge, type SubtitleFont, type SubtitleStyle } from './subStyle'

interface Props {
  style: SubtitleStyle
  /** Receives an update, so that quick successive changes all apply. */
  onChange: (update: (s: SubtitleStyle) => SubtitleStyle) => void
  /** Whether the selected track is ASS, where text settings need plain mode. */
  ass: boolean
}

const FONTS: [SubtitleFont, MessageKey][] = [
  ['sans', 'subs.font.sans'],
  ['serif', 'subs.font.serif'],
  ['mono', 'subs.font.mono'],
  ['rounded', 'subs.font.rounded'],
]

const EDGES: [SubtitleEdge, MessageKey][] = [
  ['none', 'subs.edge.none'],
  ['outline', 'subs.edge.outline'],
  ['shadow', 'subs.edge.shadow'],
]

export function formatDelay(d: number): string {
  return `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d).toFixed(1)}`
}

/** Subtitle appearance and timing, applied live. */
export function SubtitleSettings({ style, onChange, ass }: Props) {
  const { t } = useI18n()
  const set = (patch: Partial<SubtitleStyle>) => onChange((s) => ({ ...s, ...patch }))
  const textStyled = !ass || style.ass === 'plain'

  return (
    <div className="subset">
      <div className="subset__preview" style={styleVars(style) as React.CSSProperties} aria-hidden="true">
        <span className="cue">{t('subs.sample')}</span>
      </div>

      {ass && (
        <Field label={t('subs.assMode')} hint={style.ass === 'original' ? t('subs.assOriginalHint') : undefined}>
          <Segmented
            value={style.ass}
            options={[
              ['original', t('subs.assOriginal')],
              ['plain', t('subs.assPlain')],
            ]}
            onChange={(ass) => set({ ass })}
          />
        </Field>
      )}

      <fieldset className="subset__group" disabled={!textStyled}>
        <Field label={t('subs.size')} value={`${Math.round(style.size * 100)}%`}>
          <input
            type="range"
            min={SIZE_RANGE.min}
            max={SIZE_RANGE.max}
            step={SIZE_RANGE.step}
            value={style.size}
            onChange={(e) => set({ size: clamp(Number(e.target.value), SIZE_RANGE) })}
            aria-label={t('subs.size')}
          />
        </Field>

        <Field label={t('subs.font')}>
          <Segmented
            className="segmented--fonts"
            value={style.font}
            options={FONTS.map(([v, k]) => [v, t(k)])}
            render={(v) => <span style={{ fontFamily: FONT_FAMILIES[v] }}>{t('subs.fontSample')}</span>}
            onChange={(font) => set({ font })}
          />
        </Field>

        <Field label={t('subs.color')}>
          <div className="subset__swatches" role="radiogroup" aria-label={t('subs.color')}>
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={style.color === c}
                className="subset__swatch"
                style={{ background: c }}
                onClick={() => set({ color: c })}
                aria-label={c}
              />
            ))}
          </div>
        </Field>

        <Field label={t('subs.edge')}>
          <Segmented value={style.edge} options={EDGES.map(([v, k]) => [v, t(k)])} onChange={(edge) => set({ edge })} />
        </Field>

        <Field label={t('subs.background')} value={`${Math.round(style.background * 100)}%`}>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={style.background}
            onChange={(e) => set({ background: Number(e.target.value) })}
            aria-label={t('subs.background')}
          />
        </Field>

        <Field label={t('subs.position')} hint={t('subs.positionHint')}>
          <button
            type="button"
            className="subset__secondary"
            disabled={style.x === DEFAULT_STYLE.x && style.y === DEFAULT_STYLE.y}
            onClick={() => set({ x: DEFAULT_STYLE.x, y: DEFAULT_STYLE.y })}
          >
            {t('subs.positionReset')}
          </button>
        </Field>
      </fieldset>

      <Field label={t('subs.delay')} hint={t('subs.delayHint')}>
        <div className="subset__stepper">
          <button type="button" onClick={() => onChange((s) => ({ ...s, delay: clamp(s.delay - 0.1, DELAY_RANGE) }))} aria-label={t('subs.earlier')}>
            −
          </button>
          <DelayInput value={style.delay} onCommit={(delay) => set({ delay })} />
          <button type="button" onClick={() => onChange((s) => ({ ...s, delay: clamp(s.delay + 0.1, DELAY_RANGE) }))} aria-label={t('subs.later')}>
            +
          </button>
          {style.delay !== 0 && (
            <button type="button" className="subset__reset-one" onClick={() => set({ delay: 0 })}>
              {t('subs.zero')}
            </button>
          )}
        </div>
      </Field>

      <button type="button" className="subset__reset" onClick={() => onChange(() => ({ ...DEFAULT_STYLE }))}>
        {t('subs.reset')}
      </button>
    </div>
  )
}

function Field({ label, value, hint, children }: { label: string; value?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="subset__field">
      <div className="subset__label">
        <span>{label}</span>
        {value && <span className="subset__value">{value}</span>}
      </div>
      {children}
      {hint && <p className="subset__hint">{hint}</p>}
    </div>
  )
}

function Segmented<V extends string>({
  value,
  options,
  onChange,
  render,
  className = '',
}: {
  value: V
  options: [V, string][]
  onChange: (v: V) => void
  /** Custom content of a button; the label then becomes its tooltip. */
  render?: (v: V) => React.ReactNode
  className?: string
}) {
  return (
    <div className={`segmented ${className}`} role="radiogroup">
      {options.map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} title={render ? label : undefined} aria-label={render ? label : undefined}>
          {render ? render(v) : label}
        </button>
      ))}
    </div>
  )
}

/**
 * The delay in seconds as an editable field: "-1.5", "2,3" and "+4" are all
 * accepted; the value applies on Enter or when the field loses focus.
 */
function DelayInput({ value, onCommit }: { value: number; onCommit: (v: number) => void }) {
  const { t } = useI18n()
  const [draft, setDraft] = useState<string | null>(null)
  const commit = () => {
    if (draft === null) return
    const n = Number(draft.trim().replace(',', '.').replace('−', '-'))
    if (draft.trim() !== '' && Number.isFinite(n)) onCommit(clamp(n, DELAY_RANGE))
    setDraft(null)
  }
  return (
    <label className="subset__delay">
      <input
        type="text"
        inputMode="decimal"
        value={draft ?? formatDelay(value)}
        onFocus={(e) => {
          setDraft(String(value))
          requestAnimationFrame(() => e.target.select())
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            e.stopPropagation()
            setDraft(null)
          }
        }}
        aria-label={t('subs.delay')}
      />
      <span>{t('subs.secondsUnit')}</span>
    </label>
  )
}
